import { StringMap } from '@/shared/utils/map'
import { FormAttributeDefinition } from '@/shared/forms/form-attribute-definition'
import { deserialize } from '@/shared/forms/functions/deserialize'
import { serialize } from '@/shared/forms/functions/serialize'
import { Constructor } from '@/types/main'
import { RuleDecl } from 'vue/types/options'
import { createObservable } from '@/shared/utils/create-observable'
import Vue from 'vue'
import { Validator } from '@/shared/forms/validator'
import { Validation } from 'vuelidate'

type ValidationProperties<V> = Validation &
  {
    [P in keyof V]?: Validation
  } & {
    $anyDirty: boolean
    $anyError: boolean
  }

export class Form<Model extends StringMap<any>, FormModel extends StringMap<any>> {
  public dirty = false

  public get invalid(): boolean {
    return this.state.$invalid || false
  }

  public value: FormModel

  public get state(): Validation & ValidationProperties<FormModel> {
    let state: Validation & ValidationProperties<FormModel> = {} as any

    if (this.vue.$v !== undefined && this.vue.$v.form !== undefined) {
      state = this.vue.$v.form.value as Validation & ValidationProperties<FormModel>
    }

    return state || {}
  }

  private get attributes(): FormAttributeDefinition[] {
    return Object.values(Reflect.getMetadata('form', this.model.prototype))
  }

  private ignoreForValidation = false

  public constructor(private model: Constructor<FormModel>, private vue: Vue) {
    this.value = {} as any

    const value = new model()

    this.ignoreForValidation = true

    for (const attribute of this.attributes) {
      if (typeof this.value[attribute.name] === 'undefined') {
        // @ts-ignore
        this.value[attribute.name] = undefined
      }

      // Watch programmatic changes and update the touched attribute of the form control
      createObservable(this.value, attribute.name, name => {
        if (this.ignoreForValidation === false) {
          if (vue.$v !== undefined) {
            const form: any = vue.$v.form
            const field = form.value[name]

            if (isValidation(field)) {
              field.$touch()
            }
          }

          this.dirty = true
        }
      })

      if (value[attribute.name] !== undefined) {
        // @ts-ignore
        this.value[attribute.name] = value[attribute.name]
      }
    }

    this.ignoreForValidation = false
  }

  public deserialize<T>(value: T): this {
    const deserialized: FormModel = deserialize(value, this.attributes)

    this.ignoreForValidation = true

    Object.keys(deserialized).forEach(key => {
      // @ts-ignore Can be since T has to be a StringMap
      this.value[key] = deserialized[key]
    })

    this.ignoreForValidation = false

    return this
  }

  /**
   * Returns the vuelidate validation object or an empty object if it doesn't exist
   * @param name
   */
  public field(name: string): Validation {
    return (
      this.state[name] ||
      ({
        $dirty: false,
        $invalid: false,
      } as any)
    )
  }

  public reset(value: StringMap<any> = {}): void {
    for (const attribute of this.attributes) {
      // @ts-ignore
      this.value[attribute.name] = value[attribute.name]
    }

    if (this.vue.$v !== undefined) {
      this.vue.$v.$reset()
    }

    this.dirty = false
  }

  public serialize(): Model {
    return serialize(this.value, this.attributes) as Model
  }

  public validations(): RuleDecl {
    const validations: StringMap<StringMap<Validator>> = {}

    for (const attribute of this.attributes) {
      validations[attribute.name] = attribute.validators
    }

    return {
      form: {
        value: validations,
      },
    }
  }
}

function isValidation(value: any): value is Validation {
  return value !== undefined && '$invalid' in value
}
